import { Chip, Skeleton, Stack } from '@mui/material';
import { useLayoutEffect, useRef, useState } from 'react';

interface Tag {
  label: string;
}

interface TagListProps {
  tags: Tag[];
  maxVisible?: number;
  maxRows?: number;
}

export const TagListLoading = () => {
  return (
    <Stack direction="row" spacing={1}>
      <Skeleton variant="text" width={100} height={32} />
    </Stack>
  );
};

const useVisibleCount = (tags: Tag[], maxRows: number, enabled: boolean) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(tags.length);

  useLayoutEffect(() => {
    if (!enabled) {
      setVisibleCount(tags.length);
      return;
    }

    const container = containerRef.current;
    if (!container) return;

    const measure = () => {
      const chips = Array.from(
        container.querySelectorAll<HTMLElement>('[data-measure-chip]')
      );
      const summary = container.querySelector<HTMLElement>(
        '[data-measure-summary]'
      );

      if (chips.length === 0) {
        setVisibleCount(0);
        return;
      }

      const containerWidth = container.clientWidth;

      if (containerWidth === 0) {
        setVisibleCount(tags.length);
        return;
      }

      const rowTops: number[] = [];
      for (const chip of chips) {
        const top = chip.offsetTop;
        if (rowTops.length === 0 || top > rowTops[rowTops.length - 1]) {
          rowTops.push(top);
        }
      }

      if (rowTops.length <= maxRows) {
        setVisibleCount(tags.length);
        return;
      }

      const maxTop = rowTops[maxRows - 1];
      const summaryWidth = summary?.offsetWidth ?? 0;
      const gap = parseFloat(getComputedStyle(container).columnGap) || 0;

      let count = 0;
      for (const chip of chips) {
        if (chip.offsetTop > maxTop) break;

        const isLastRow = chip.offsetTop === maxTop;
        const roomForSummary =
          chip.offsetLeft + chip.offsetWidth + gap + summaryWidth <=
          containerWidth;

        if (isLastRow && !roomForSummary) break;
        count += 1;
      }

      setVisibleCount(Math.max(count, 1));
    };

    measure();

    if (typeof document !== 'undefined' && 'fonts' in document) {
      document.fonts.ready.then(measure).catch(() => { });
    }

    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
  }, [tags, maxRows, enabled]);

  return { containerRef, visibleCount };
};

export const TagList = ({ tags, maxVisible, maxRows = 3 }: TagListProps) => {
  const dynamic = maxVisible === undefined;
  const { containerRef, visibleCount } = useVisibleCount(
    tags,
    maxRows,
    dynamic
  );

  const limit = dynamic ? visibleCount : maxVisible;
  const visibleTags = tags.slice(0, limit);
  const hiddenCount = tags.length - visibleTags.length;

  return (
    <Stack sx={{ position: 'relative', width: '100%' }}>
      {dynamic && (
        <Stack
          ref={containerRef}
          direction="row"
          gap={1}
          flexWrap="wrap"
          aria-hidden
          sx={{
            position: 'absolute',
            visibility: 'hidden',
            pointerEvents: 'none',
            inset: 0,
          }}
        >
          {tags.map((tag) => (
            <Chip
              key={tag.label}
              data-measure-chip
              label={tag.label}
            />
          ))}
          <Chip
            data-measure-summary
            label={`+ ${tags.length} habilidades`}
          />
        </Stack>
      )}

      <Stack direction="row" gap={1} flexWrap="wrap">
        {visibleTags.map((tag) => (
          <Chip
            key={tag.label}
            label={tag.label}
          />
        ))}
        {hiddenCount > 0 && (
          <Chip
            label={`+ ${hiddenCount} ${hiddenCount === 1 ? 'habilidade' : 'habilidades'}`}
          />
        )}
      </Stack>
    </Stack>
  );
};
